import { supabase } from '@/lib/supabase';

export interface PublicInstagramPost {
  id: string;
  instagram_username: string;
  instagram_post_id?: string;
  shortcode?: string;
  imagekit_url: string;
  original_image_url?: string;
  post_type?: string;
  caption?: string;
  likes_count?: number;
  comments_count?: number;
  timestamp?: string;
  dimensions_width?: number;
  dimensions_height?: number;
  video_url?: string;
  video_view_count?: number;
  post_data?: any;
  created_at?: string;
  updated_at?: string;
}

/**
 * 특정 인스타그램 사용자의 게시물을 가져옵니다.
 * @param username 인스타그램 사용자명
 * @returns 인스타그램 게시물 배열
 */
export async function fetchInstagramPosts(
  username: string
): Promise<PublicInstagramPost[]> {
  try {
    // public_instagram_posts 테이블에서 해당 사용자명으로 게시물 조회
    const { data: posts, error } = await supabase
      .from('public_instagram_posts')
      .select('*')
      .eq('instagram_username', username)
      .order('timestamp', { ascending: false });
      // limit 제거하여 모든 게시물 가져옴
  
    if (error) {
      console.error('게시물 조회 중 오류 발생:', error);
      return [];
    }
    
    if (!posts || posts.length === 0) {
      console.log(`${username}의 게시물이 없습니다.`);
      return [];
    }
    
    // imagekit_url이 있는 게시물만 필터링
    const validPosts = posts.filter(post => post.imagekit_url);
    
    if (validPosts.length === 0) {
      console.log(`${username}의 유효한 이미지가 있는 게시물이 없습니다.`);
      return [];
    }
    
    return validPosts;
  } catch (error) {
    console.error('fetchInstagramPosts 함수 오류:', error);
    return [];
  }
}

/**
 * 이미지 요청 패턴을 감지하는 유틸리티 함수
 * @param message 사용자 메시지
 * @returns 이미지 요청 여부
 */
export function isImageRequest(message: string): boolean {
  const lowerMessage = message.toLowerCase();
  
  // 이미지 요청과 관련된 키워드/패턴
  const imageRequestPatterns = [
    '사진', '사진 보여', '사진 보여줘', '사진 좀', '사진 좀 보여줘',
    '사진 있어?', '사진 있니?', '사진 보낼래?', '사진 보내줘',
    '피드', '피드 보여줘', '인스타', '인스타 보여줘',
    '이미지', '이미지 보여줘', '셀카', '셀카 보여줘', '셀피',
    '게시물', '게시물 보여줘', '포스트', '포스트 보여줘',
    '보여줘'
  ];
  
  // 패턴 중 하나라도 포함되어 있으면 이미지 요청으로 간주
  return imageRequestPatterns.some(pattern => lowerMessage.includes(pattern));
}

/**
 * 게시물에서 사용자 요청에 가장 적합한 이미지를 선택합니다.
 * @param posts 인스타그램 게시물 배열
 * @param message 사용자 메시지
 * @returns 선택된 게시물
 */
export function selectAppropriatePost(
  posts: PublicInstagramPost[],
  message: string
): PublicInstagramPost | null {
  if (!posts || posts.length === 0) {
    return null;
  }
  
  const lowerMessage = message.toLowerCase();
  
  // 특정 키워드에 기반한 게시물 선택 로직
  if (lowerMessage.includes('셀카') || lowerMessage.includes('셀피')) {
    const selfiePost = posts.find(post => 
      post.caption && 
      (post.caption.toLowerCase().includes('셀카') || 
       post.caption.toLowerCase().includes('셀피') ||
       post.caption.toLowerCase().includes('selfie'))
    );
    
    if (selfiePost) {
      return selfiePost;
    }
  }
  else if (lowerMessage.includes('여행') || lowerMessage.includes('장소') || lowerMessage.includes('어디')) {
    const locationPost = posts.find(post => 
      post.caption && 
      (post.caption.toLowerCase().includes('여행') || 
       post.caption.toLowerCase().includes('장소') ||
       post.caption.toLowerCase().includes('여기'))
    );
    
    if (locationPost) {
      return locationPost;
    }
  }
  else if (lowerMessage.includes('옛날') || lowerMessage.includes('예전')) {
    // 가장 오래된 게시물을 선택
    if (posts.length > 1) {
      return posts[posts.length - 1];
    }
  }
  else if (lowerMessage.includes('인기') || lowerMessage.includes('좋아요')) {
    const popularPost = posts.reduce((prev, current) => 
      (prev.likes_count || 0) > (current.likes_count || 0) ? prev : current
    );
    return popularPost;
  }
  else if (lowerMessage.includes('랜덤') || lowerMessage.includes('아무거나') || lowerMessage.includes('다른')) {
    const randomIndex = Math.floor(Math.random() * posts.length);
    return posts[randomIndex];
  }
  
  // 기본적으로 랜덤하게 선택 (항상 최신 게시물만 보여주지 않도록)
  const randomIndex = Math.floor(Math.random() * Math.min(posts.length, 5)); // 최신 5개 중에서 랜덤 선택
  return posts[randomIndex];
}